// 193 -- bulk time-shift dialog, shared by the desktop schedule grid and the
// mobile schedule. Two modes: "from" (a running-late day: shift the anchor
// match + everything after it, optionally only its field) and "selection"
// (move an explicit set of matches together). Time-only -- courts never
// change here. Applies optimistically; the server call is one transaction.

import { type Dispatch, type SetStateAction, useMemo, useState } from "react";
import type { MatchPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { notifySaving, notifySaved } from "@/lib/save-status";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

export type ShiftMode =
  | { kind: "from"; match: MatchPublic }
  | { kind: "selection"; matchIds: string[] };

const DELTA_CHIPS = [-15, -10, -5, 5, 10, 15];

export function ShiftMatchesDialog({ eventId, matches, mode, courtLabel, setMatches, refreshMatches, onClose }: {
  eventId: string;
  matches: MatchPublic[];
  mode: ShiftMode;
  courtLabel: string;
  setMatches: Dispatch<SetStateAction<MatchPublic[]>>;
  refreshMatches: () => Promise<void>;
  onClose: () => void;
}) {
  const anchorHasCourt = mode.kind === "from" && mode.match.event_court_id !== null;
  const [scope, setScope] = useState<"court" | "all">(anchorHasCourt ? "court" : "all");
  const [delta, setDelta] = useState<number>(10);
  const [customStr, setCustomStr] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const affectedIds = useMemo(() => {
    if (mode.kind === "selection") return mode.matchIds;
    const fromMs = new Date(mode.match.starts_at).getTime();
    return matches
      .filter((m) => new Date(m.starts_at).getTime() >= fromMs)
      .filter((m) => (scope === "court" ? m.event_court_id === mode.match.event_court_id : true))
      .map((m) => m.id);
  }, [mode, matches, scope]);

  function pickCustom(value: string) {
    setCustomStr(value);
    const n = Math.round(Number(value));
    if (Number.isFinite(n) && n !== 0) setDelta(n);
  }

  async function apply() {
    if (delta === 0 || affectedIds.length === 0 || saving) return;
    setSaving(true);
    setError(null);
    const deltaMs = delta * 60_000;
    const idSet = new Set(affectedIds);
    setMatches((prev) => prev.map((m) => idSet.has(m.id)
      ? {
        ...m,
        starts_at: new Date(new Date(m.starts_at).getTime() + deltaMs).toISOString(),
        ends_at: new Date(new Date(m.ends_at).getTime() + deltaMs).toISOString(),
      }
      : m));
    notifySaving();
    try {
      if (mode.kind === "from") {
        await api.shiftMatches(eventId, {
          delta_mins: delta,
          from: mode.match.starts_at,
          ...(scope === "court" && mode.match.event_court_id ? { event_court_id: mode.match.event_court_id } : {}),
        });
      } else {
        await api.shiftMatches(eventId, { delta_mins: delta, match_ids: mode.matchIds });
      }
      notifySaved();
      onClose();
    } catch (e) {
      refreshMatches();
      setError(e instanceof api.ApiError ? e.message : "Failed to shift matches");
      setSaving(false);
    }
  }

  const noun = courtLabel.toLowerCase();
  const deltaLabel = delta > 0 ? `+${delta}` : `${delta}`;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center overflow-y-auto bg-black/60 p-4 sm:items-center"
      data-dialog="shift-matches"
    >
      <Card className="max-h-[90vh] w-full max-w-md overflow-y-auto">
        <CardHeader>
          <CardTitle>
            {mode.kind === "from" ? "Running late? Shift the schedule" : `Move ${mode.matchIds.length} selected matches`}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {mode.kind === "from" && (
            <p className="text-sm text-muted-foreground">
              Shifts <span className="font-medium text-foreground">{mode.match.name}</span> and every match after it.
            </p>
          )}
          {error && <p className="text-sm text-destructive" data-error>{error}</p>}

          <div className="flex flex-wrap items-center gap-2">
            {DELTA_CHIPS.map((d) => (
              <button
                key={d}
                type="button"
                data-action="shift-delta"
                data-delta={d}
                onClick={() => { setDelta(d); setCustomStr(""); }}
                className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${delta === d && customStr === "" ? "border-primary bg-primary/10 text-primary" : "border-input hover:border-primary"}`}
              >
                {d > 0 ? `+${d}` : d} min
              </button>
            ))}
            <input
              type="number"
              inputMode="numeric"
              placeholder="Custom"
              title="Custom minutes (negative moves earlier)"
              value={customStr}
              onChange={(e) => pickCustom(e.target.value)}
              data-field="shift-custom"
              className="w-24 rounded-md border border-input bg-background px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {mode.kind === "from" && anchorHasCourt && (
            <div className="flex gap-2" role="radiogroup" aria-label="Scope">
              {([
                { key: "court", label: `This ${noun} only` },
                { key: "all", label: "Whole event" },
              ] as const).map((o) => (
                <button
                  key={o.key}
                  type="button"
                  role="radio"
                  aria-checked={scope === o.key}
                  data-action="shift-scope"
                  data-scope={o.key}
                  onClick={() => setScope(o.key)}
                  className={`rounded-md border px-3 py-1.5 text-sm transition ${scope === o.key ? "border-primary bg-primary/10 text-primary" : "border-input hover:border-primary"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}

          <p className="text-sm text-muted-foreground" data-field="shift-preview">
            Moves <span className="font-medium text-foreground">{affectedIds.length}</span>{" "}
            {affectedIds.length === 1 ? "match" : "matches"} by{" "}
            <span className="font-medium text-foreground">{deltaLabel} min</span>.
          </p>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" data-action="shift-cancel" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" data-action="apply-shift" onClick={apply} disabled={saving || delta === 0 || affectedIds.length === 0}>
              {saving ? "Shifting..." : "Shift matches"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
